import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ProfileService } from '../../services/profile/profile.service';
import { AlertBannerService, AlertType } from '../../lib/alert-banner';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-mobile-verification-modal',
  templateUrl: './mobile-verification-modal.component.pug',
  styleUrls: ['./mobile-verification-modal.component.scss'],
  standalone: false
})
export class MobileVerificationModalComponent implements OnInit, OnDestroy {
  @Input() countryCode: string = '';
  @Input() phoneNumber: string = '';
  @Input() lastResendTimestamp: number | null = null;

  public event: EventEmitter<any> = new EventEmitter();
  @Output() resendEvent: EventEmitter<number> = new EventEmitter();

  // OTP configuration
  readonly OTP_LENGTH = 6;
  private readonly RESEND_COOLDOWN_MS = 60000; // 60 seconds

  verificationCode: string = '';
  type: string = 'text';
  isLoading: boolean = false;
  errorFeedback: boolean = false;
  errors = [
    { name: 'invalidCode', msg: $localize`The code is incorrect or has expired, please try again.`, enabled: false }
  ];

  // Resend cooldown state (60 seconds)
  resendDisabled: boolean = false;
  codeResent: boolean = false;
  private resendTimeoutId: any;
  private destroy$ = new Subject<void>();

  get isValidOtp(): boolean {
    return this.verificationCode.length === this.OTP_LENGTH;
  }

  constructor(
    public _bsModalRef: BsModalRef,
    private profileService: ProfileService,
    private alertBannerService: AlertBannerService,
  ) {
  }

  ngOnInit(): void {
    // Check if we're still within cooldown from a previous resend
    if (this.lastResendTimestamp) {
      const elapsed = Date.now() - this.lastResendTimestamp;
      const remaining = this.RESEND_COOLDOWN_MS - elapsed;
      if (remaining > 0) {
        this.resendDisabled = true;
        this.codeResent = true;
        this.resendTimeoutId = setTimeout(() => {
          this.resendDisabled = false;
        }, remaining);
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.resendTimeoutId) {
      clearTimeout(this.resendTimeoutId);
    }
  }

  resendCode() {
    if (this.resendDisabled || this.isLoading) {
      return;
    }

    this.resetError();
    this.verificationCode = '';
    this.isLoading = true;

    this.profileService.triggerMobileVerification(this.countryCode, this.phoneNumber)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isLoading = false;
          this.codeResent = true;
          this.resendDisabled = true;
          // Emit timestamp to parent for persistence across modal instances
          const timestamp = Date.now();
          this.resendEvent.emit(timestamp);
          // Re-enable after cooldown
          this.resendTimeoutId = setTimeout(() => {
            this.resendDisabled = false;
          }, this.RESEND_COOLDOWN_MS);
        },
        error: () => {
          this.isLoading = false;
          // Resend failure - show generic error banner
          this.alertBannerService.append([{
            type: AlertType.DANGER,
            message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
            keepOnRouteChange: true
          }]);
        }
      });
  }

  verify() {
    if (!this.verificationCode || this.isLoading) {
      return;
    }

    this.resetError();
    this.isLoading = true;

    this.profileService.verifyMobile(this.verificationCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          if (response?.verified) {
            this.event.emit({
              verified: true,
              mobile: response.mobile,
              mobile_verified: response.mobile_verified
            });
            this._bsModalRef.hide();
          } else {
            this.setError();
          }
        },
        error: (err) => {
          this.isLoading = false;
          // Check for otp_incorrect error code from backend
          if (err?.error?.data?.error_code === 'otp_incorrect') {
            this.setError();
          } else {
            // Generic error - show error banner
            this.alertBannerService.append([{
              type: AlertType.DANGER,
              message: $localize`Oops! Something went wrong. Our developers have been notified. Feel free to try again.`,
              keepOnRouteChange: true
            }]);
          }
        }
      });
  }

  cancel() {
    this.event.emit({ cancelled: true });
    this._bsModalRef.hide();
  }

  textValueChanged(value: string) {
    // Only allow digits, limit to OTP_LENGTH characters
    const digitsOnly = (value || '').replace(/\D/g, '').slice(0, this.OTP_LENGTH);
    this.verificationCode = digitsOnly;
    // Clear error when user starts typing a new code
    if (this.errorFeedback) {
      this.resetError();
    }
  }

  /**
   * Sets the error state to show the invalid code message.
   */
  setError() {
    this.errorFeedback = true;
    this.errors = this.errors.map(e => ({ ...e, enabled: e.name === 'invalidCode' }));
  }

  /**
   * Resets the error state.
   */
  resetError() {
    this.errorFeedback = false;
    this.errors = this.errors.map(e => ({ ...e, enabled: false }));
  }
}


